import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { gql, useMutation } from '@common/hooks';
import { Col, Row, Button, Form, List, Tooltip } from 'antd';
import {
  Drawer,
  SubmitButton,
  ErrorHandler,
  ButtonGroup,
} from '@common/components';
import { CreateWorksSubmittalPacket } from 'works/graphql/works-submittal-packet';
import { createMessage } from 'works/legacy/lib/message-helper';
import { SubmittalPacketsUploadInput } from 'works/components/submittal-packets-upload-input';
import { RecentlyUploadedList } from './submittal-packets-management-drawer-recently-uploaded-list';

const archive = gql`
  mutation ArchiveSubmittalPacket($submittalPacketId: ID!) {
    archiveSubmittalPacket(submittalPacketId: $submittalPacketId) {
      submittalPacket {
        id
        archived
        files {
          id
          filename
          downloadUrl
          createdAt
        }
      }
    }
  }
`;

function hasFormAnyError(form) {
  const [fieldsErrors] = form.getFieldsError() || [];
  return fieldsErrors?.errors?.length > 0;
}

function getAvailableFileNames(worksSubmittalPackets) {
  return worksSubmittalPackets
    .filter((_submittalPacket) => !_submittalPacket?.archived)
    .map((_submittalPacket) => {
      const [file] = _submittalPacket?.files || [];
      return file?.filename || '';
    });
}

export function SubmittalPacketsManagementDrawer({
  visible,
  onClose,
  selectedWorksProfile,
}) {
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);

  const { worksSubmittalPackets } = selectedWorksProfile;

  const availableFileNames = getAvailableFileNames(worksSubmittalPackets);

  const [createWorkSubmittalPacket, { error: submittalPacketError, loading }] =
    useMutation(CreateWorksSubmittalPacket, {
      refetchQueries: ['supplierDetailedCandidateProfile'],
    });

  const [archiveSubmittalPacket, { error: archiveError }] = useMutation(
    archive,
    {
      onCompleted: (data) => {
        if (!data?.archiveSubmittalPacket) {
          return;
        }

        createMessage('success', 'Submittal packet was archived successfully!');
      },
    }
  );

  const anyError = submittalPacketError || archiveError;

  if (anyError) {
    return (
      <ErrorHandler
        error={anyError}
        messageType="contextual"
        log="StaffingRequestManageSubmittalPacketsDrawer"
      />
    );
  }

  function resetForm() {
    form.resetFields();
    setIsFormValid(false);
  }

  function onInputValueChange() {
    setTimeout(() => {
      const hasError = hasFormAnyError(form);
      setIsFormValid(!hasError);
    });
  }

  const saveSubmittalPackage = async ({ submittalPacket }) => {
    await createWorkSubmittalPacket({
      variables: {
        worksProfileId: selectedWorksProfile.id,
        file: submittalPacket.file,
      },
    });

    createMessage('success', 'Changes saved successfully!');
    _onClose();
  };

  function _onClose() {
    resetForm();
    onClose();
  }

  function getSubmmitButton() {
    return (
      <SubmitButton
        size="large"
        onClick={form.submit}
        loading={loading}
        disabled={!isFormValid}
      >
        Upload
      </SubmitButton>
    );
  }

  return (
    <>
      <Drawer
        className="submittal-packets-management-drawer"
        open={visible}
        placement="right"
        onClose={_onClose}
        width={600}
        title="Manage Submittal Packets"
        footer={
          <ButtonGroup>
            <Button size="large" key="back" onClick={_onClose}>
              Close
            </Button>
            {isFormValid ? (
              getSubmmitButton()
            ) : (
              <Tooltip title="Please make sure you have selected a correct file to upload.">
                {getSubmmitButton()}
              </Tooltip>
            )}
          </ButtonGroup>
        }
      >
        <Form onFinish={saveSubmittalPackage} form={form}>
          <Row>
            <Col xs={{ span: 24 }}>
              <h4 className="submittal-packets__heading text-heavy">
                Upload New
              </h4>
              <SubmittalPacketsUploadInput
                availableFileNames={availableFileNames}
                onChange={onInputValueChange}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={{ span: 24 }}>
              <h4 className="submittal-packets__heading text-heavy">
                Recently Uploaded
              </h4>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <List
                bordered
                dataSource={worksSubmittalPackets}
                renderItem={(submittalPacket) => (
                  <RecentlyUploadedList
                    submittalPacket={submittalPacket}
                    archiveSubmittalPacket={archiveSubmittalPacket}
                  />
                )}
              />
            </Col>
          </Row>
        </Form>
      </Drawer>
    </>
  );
}

SubmittalPacketsManagementDrawer.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  selectedWorksProfile: PropTypes.shape({
    id: PropTypes.string,
    worksSubmittalPackets: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        archived: PropTypes.bool,
        files: PropTypes.arrayOf(
          PropTypes.shape({
            createdAt: PropTypes.string,
            downloadUrl: PropTypes.string,
            filename: PropTypes.string,
            id: PropTypes.string,
          })
        ),
      })
    ),
  }),
};
